/**
 * Component: UserGenderStatistic
 * Horizontal stacked bar distribution for gender statistic with legends.
 */

import { CustomerActiveIcon, ExternalIcon } from "@/assets/svg";
import {
  BarGraph,
  type BarGraphSegment,
} from "@/components/ui/bar-graph/bar-graph";
import type { GenderDemographicsData } from "../../../../domain/model/DashboardModel";

export interface UserGenderStatisticProps {
  data: GenderDemographicsData | null;
}

export const UserGenderStatistic = ({ data }: UserGenderStatisticProps): React.JSX.Element => {
  if (!data) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 pb-3 flex flex-col shadow-sm">
        <div className="flex justify-between items-center mb-3">
          <h3 className="m-0 text-slate-900 font-bold text-base leading-6">User Gender Statistic</h3>
        </div>
        <div className="flex items-center justify-center h-[80px] text-gray-500">
          No gender data available
        </div>
      </div>
    );
  }

  const genderSegments: ReadonlyArray<BarGraphSegment> = [
    {
      label: "Male",
      value: data.male,
      color: "#000f3d",
    },
    {
      label: "Female",
      value: data.female,
      color: "#ffb800",
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 pb-3 flex flex-col shadow-[0_10px_15px_-3px_rgba(15,23,42,0.05),0_4px_6px_-4px_rgba(15,23,42,0.03)]">
      <div className="flex justify-between items-center mb-3">
        <h3 className="m-0 text-slate-900 font-bold text-base leading-6">User Gender Statistic</h3>
        <button
          className="bg-transparent border-none p-0 cursor-pointer flex items-center justify-center hover:opacity-80 focus:outline-none active:opacity-60"
          aria-label="Open external link"
          type="button"
        >
          <ExternalIcon />
        </button>
      </div>
      <div className="py-1 pb-2">
        <BarGraph
          segments={genderSegments}
          ariaLabel="User gender distribution horizontal bar graph"
          className="h-8 w-full"
        />
      </div>
      <div className="flex justify-between items-center w-full mt-2 text-xs p-0">
        <div className="flex items-center gap-2">
          <div className="font-normal text-sm leading-5 tracking-[0.005em] text-gray-900">{genderSegments[0].label}</div>
          <div className="flex items-center gap-1">
            <span className="font-bold text-sm leading-5 tracking-[0.005em] text-gray-900">
              {genderSegments[0].value.toLocaleString()}
            </span>
            <span className="inline-flex items-center justify-center [&>svg]:w-3.5 [&>svg]:h-3.5">
              <CustomerActiveIcon />
            </span>
          </div>
        </div>
        <div className="w-px h-5 bg-gray-200" />
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center justify-center [&>svg]:w-3.5 [&>svg]:h-3.5">
              <CustomerActiveIcon />
            </span>
            <span className="font-bold text-sm leading-5 tracking-[0.005em] text-gray-900">
              {genderSegments[1].value.toLocaleString()}
            </span>
          </div>
          <div className="font-normal text-sm leading-5 tracking-[0.005em] text-gray-900">{genderSegments[1].label}</div>
        </div>
      </div>
    </div>
  );
};
